import { Card, CardContent, Grid } from '@mui/material';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ExperimentForm, { FieldKeys } from '../../Components/BodyComponent/Experiments/ExperimentForm';
import { experimentsService } from '../../Services/Experiments.services';
import styles from './ExperimentDocsPage.module.css';
import { Header } from '../../Components/Shared/Header/Header';
import { useEffect, useState } from 'react';
import SkeletonComponent from '../../Components/SkeletonComponent';
import ExperimentEntitiesTable from '../../Components/BodyComponent/Experiments/ExperimentEntitiesTable';
import ExperimentTitle from '../../Components/BodyComponent/Experiments/ExperimentTitle';
import { UploadArea } from '../../Components/Shared/UploadArea/UploadArea';
import ExperimentBreadcrumbs from '../../Components/BodyComponent/Experiments/ExperimentBreadcrumbs';

function ExperimentDocsPage() {
    const { experimentId } = useParams();
    const navigate = useNavigate();
    const { isAdmin } = useSelector((state) => state.auth);
    const [experiment, setExperiment] = useState(null);
    const [isDocumentsLoading, setIsDocumentsLoading] = useState(true);
    const [documents, setDocuments] = useState([]);

    const fetchDocuments = async () => {
        setIsDocumentsLoading(true);

        try {
            const documents = await experimentsService.getExperimentDocuments(experimentId);
            setDocuments(documents);
        } catch (err) {
            console.error('Error fetching documents:', err);
            setDocuments([]);
        } finally {
            setIsDocumentsLoading(false);
        }
    };

    useEffect(() => {
        const fetchExperimentDetails = async () => {
            try {
                const experiment = await experimentsService.getExperiment(experimentId);
                setExperiment(experiment);
            } catch (err) {
                toast.error(`Cannot load experiment details: ${err.message}`);
            }
        };

        if (isAdmin) {
            fetchExperimentDetails();
            fetchDocuments();
        }
    }, [experimentId, isAdmin]);

    if (!isAdmin) {
        return <Navigate to="/experiments" />;
    }

    const handleUpdateExperiment = async (experimentData) => {
        try {
            const experiment = await experimentsService.updateExperiment(experimentId, experimentData);
            setExperiment(experiment);
        } catch (err) {
            console.error('Error updating experiment:', err);
        }
    };

    const handleFileUpload = async (files) => {
        try {
            await experimentsService.uploadExperimentDocuments(experimentId, files);
            fetchDocuments();
        } catch (err) {
            console.error('Error uploading documents:', err);
        }
    };

    const handleAddRun = async () => {
        try {
            const newRun = await experimentsService.createExperimentRun(experimentId);

            if (newRun?.id) {
                navigate(`/experiments/${experimentId}/runs/${newRun.id}`);
            }
        } catch (error) {
            console.error('Error creating experiment:', error);
        }
    };

    const handleDocumentDelete = async (documentId) => {
        try {
            await experimentsService.deleteExperimentDocument(experimentId, documentId);
            setDocuments((documents) => documents.filter((doc) => doc.id !== documentId));
        } catch (err) {
            console.error('Error deleting the run:', err);
        } finally {
            setIsDocumentsLoading(false);
        }
    };

    const handleGoToEvaluation = () => {
        navigate(`/experiments/${experimentId}/evaluation`);
    };

    const handleViewDocument = async (documentId) => {
        try {
            const { data, contentType } = await experimentsService.downloadExperimentDocument(experimentId, documentId);
            const blob = new Blob([data], { type: contentType });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
        } catch (e) {
            console.error(e);
        }
    };

    return (
        <Grid item xs={12} sm={12} md={12} sx={{ mt: 2, mb: 2 }}>
            <Card component={Paper}>
                <CardContent>
                    <div className="header">
                        <Header
                            title={(
                                <ExperimentBreadcrumbs
                                    crumbs={[
                                        {
                                            label: 'Experiments',
                                            href: '/experiments',
                                        },
                                        {
                                            label: (<ExperimentTitle experimentId={experimentId} />),
                                        },
                                    ]}
                                    pageTitle="Docs"
                                />
                            )}
                            actionButtons={[
                                (
                                    <Button
                                        key="add-run-button"
                                        variant="outlined"
                                        sx={{ ml: 2 }}
                                        onClick={handleAddRun}
                                    >
                                        Add Run
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="go-to-runs-button"
                                        variant="outlined"
                                        onClick={() => {
                                            navigate(`/experiments/${experimentId}/runs`);
                                        }}
                                    >
                                        Runs
                                    </Button>
                                ),
                                (
                                    <Button
                                        key="go-to-evaluation-button"
                                        variant="contained"
                                        sx={{ ml: 2 }}
                                        onClick={handleGoToEvaluation}
                                    >
                                        Evaluate
                                    </Button>
                                ),
                            ]}
                        />
                    </div>
                    <div className={styles.formAndTable}>
                        <div className={`${styles.form} ${styles.section}`}>
                            {
                                !experiment
                                    ? <SkeletonComponent />
                                    : (
                                        <ExperimentForm
                                            initialValues={{
                                                [FieldKeys.name]: experiment.name,
                                            }}
                                            onSave={handleUpdateExperiment}
                                        />
                                    )
                            }
                            <div className={styles.uploadArea}>
                                <UploadArea
                                    onUpload={handleFileUpload}
                                    acceptedTypes={{ 'application/pdf': ['.pdf'] }}
                                />
                            </div>
                        </div>
                        <div className={styles.table}>
                            {isDocumentsLoading
                                ? (
                                    <SkeletonComponent />
                                ) : (
                                    <div className={styles.section}>
                                        <ExperimentEntitiesTable
                                            documents={documents}
                                            onDocumentDelete={handleDocumentDelete}
                                            onPreview={handleViewDocument}
                                        />
                                    </div>
                                )}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </Grid>
    );
}

export default ExperimentDocsPage;
